import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Stack } from "@mui/material";
import { PendingChangeType } from "@repo/api";
import {
    changeContributionStateSelector,
    preselectedContributionSelector,
    setChangeContributionState,
    useAppDispatch,
    useAppSelector,
    usePossibleTaxSavingFormulaAfterNovel,
    useYearlyStateContributionFormulaAfterNovel,
} from "@repo/logic";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { NumberToCZK } from "../../helpers";
import { useScrollFirstError } from "../../hooks/useScrollFirstError";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { Card } from "../Cards";
import { SliderInput, SwitchInput } from "../Form";
import { FormError } from "../Form/FormError";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { BodyTextM, HeadingXS } from "../Typography";
import {
    changeContributionFormDefaultValues,
    ChangeContributionFormValues,
    useChangeContributionSchema,
} from "./ChangeContributionPage.helpers";
import * as Styled from "./ChangeContributionPage.styled";
import { ChangePensionContributionStepExtraProps } from "./ChangePensionContribution.types";

type ChangeContributionPageProps = DefaultStepProps & ChangePensionContributionStepExtraProps;

const MIN_CONTRIBUTION = 100;
const MAX_CONTRIBUTION = 10000;

export const ChangeContributionPage: FC<ChangeContributionPageProps> = ({
    setContinueCondition: stepperContinueCondition,
    queries: { usePensionDetail, useAllowedChanges },
    contractId,
    contractType,
    scrollToEmployerSection,
}) => {
    const { t } = useTypedTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const unchangedErrorRef = useRef<HTMLDivElement | null>(null);
    const employerSectionRef = useRef<HTMLDivElement | null>(null);
    const reduxState = useAppSelector(changeContributionStateSelector);
    const preselectedContribution = useAppSelector(preselectedContributionSelector);
    const dispatch = useAppDispatch();

    const { data: allowedChangesData, isLoading: isLoadingAllowedChanges } =
        useAllowedChanges?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) } }) ?? {};

    const { data: pensionDetailData, isLoading: isLoadingPensionDetail } =
        usePensionDetail?.({ id: contractId ?? "", options: { enabled: Boolean(contractId) }, type: contractType }) ??
        {};

    const isLoading = isLoadingPensionDetail || isLoadingAllowedChanges;

    const currentValues = useMemo(() => {
        const contributionPendingChange = allowedChangesData?.pendingChanges?.find(
            (change) => change.changeType === PendingChangeType.ChangeContribution,
        );

        if (contributionPendingChange) {
            return {
                employerContributes:
                    contributionPendingChange.detail?.willEmployerContribute ??
                    pensionDetailData?.contract.doesEmployerContribute,
                currentContribution:
                    contributionPendingChange.detail?.newClientContribution ??
                    pensionDetailData?.contract.monthlyContributionClient,
            };
        }
        return {
            employerContributes: pensionDetailData?.contract.doesEmployerContribute,
            currentContribution: pensionDetailData?.contract.monthlyContributionClient,
        };
    }, [pensionDetailData, allowedChangesData]);

    const schema = useChangeContributionSchema();
    const methods = useForm<ChangeContributionFormValues>({
        defaultValues: changeContributionFormDefaultValues,
        mode: "onChange",
        resolver: yupResolver(schema),
        // Prevent RHF from auto focusing the first invalid field (which triggers an instant native scroll)
        // Our custom useScrollFirstError handles smooth scrolling explicitly.
        shouldFocusError: false,
    });

    const { handleSubmit, formState, watch, setValue, reset } = methods;
    const contributionSlider = watch("newContributionSliderField");
    const contributionText = watch("newContributionTextField");
    const employerContributes = watch("employerContributes");

    const isModified = useMemo(() => {
        return (
            Number(contributionSlider) !== Number(currentValues.currentContribution) ||
            Number(contributionText) !== Number(currentValues.currentContribution) ||
            Boolean(employerContributes) !== Boolean(currentValues.employerContributes)
        );
    }, [contributionSlider, contributionText, employerContributes, currentValues]);

    const initializedRef = useRef(false);

    //When data are loaded for the first time, set initial form values
    useEffect(() => {
        // Only initialize the form once to prevent overwriting user input on refetch
        if (initializedRef.current) return;
        if (currentValues.currentContribution === undefined) return;
        initializedRef.current = true;

        reset({
            newContributionSliderField: currentValues.currentContribution,
            newContributionTextField: currentValues.currentContribution,
            employerContributes: currentValues.employerContributes,
        });
        //Set values from Redux when available
        if (reduxState) {
            setValue("newContributionSliderField", reduxState.newMonthlyContribution);
            setValue("newContributionTextField", reduxState.newMonthlyContribution);
            setValue("employerContributes", reduxState.newEmployerContribute);
        } else if (preselectedContribution !== undefined) {
            setValue("newContributionSliderField", preselectedContribution);
            setValue("newContributionTextField", preselectedContribution);
        }
    }, [reset, setValue, currentValues, reduxState, preselectedContribution]);

    useScrollFirstError<ChangeContributionFormValues>(
        { formState },
        {
            behavior: "smooth",
            root: containerRef.current ?? undefined,
            topOffset: 100,
        },
        { auto: true },
    );

    const validateAndSubmit = useCallback(async () => {
        if (!isModified) {
            //No change - scroll to error
            unchangedErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            return false;
        }

        let canContinue = false;
        await handleSubmit(
            //onValid form
            async (data) => {
                //Dispatch changes to Redux
                dispatch(
                    setChangeContributionState({
                        newEmployerContribute: Boolean(data.employerContributes),
                        newMonthlyContribution: Number(data.newContributionSliderField),
                        currentMonthlyContribution: Number(currentValues.currentContribution),
                        currentEmployerContribute: Boolean(currentValues.employerContributes),
                    }),
                );
                canContinue = true;
            },
            //onInvalid form
            () => {
                canContinue = false;
            },
        )();

        return canContinue;
    }, [currentValues.currentContribution, currentValues.employerContributes, dispatch, handleSubmit, isModified]);

    //Register form validation as the stepper's continue condition so it runs before advancing to the next step
    useEffect(() => {
        stepperContinueCondition(validateAndSubmit);
    }, [stepperContinueCondition, validateAndSubmit]);

    const possibleGovContribution = useYearlyStateContributionFormulaAfterNovel(Number(contributionSlider));
    const possibleTaxSaving = usePossibleTaxSavingFormulaAfterNovel(Number(contributionSlider));

    //Scroll to employer section if indicated
    useEffect(() => {
        if (scrollToEmployerSection && employerSectionRef.current) {
            employerSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [scrollToEmployerSection]);

    const currentEmployerContributionText = useMemo(() => {
        if (isLoading) {
            return <Styled.InlineBlockSkeleton />;
        }
        return currentValues.employerContributes === true
            ? t("employerContributeDialog.contribute")
            : t("employerContributeDialog.dontContribute");
    }, [isLoading, currentValues.employerContributes, t]);

    return (
        <FormProvider {...methods}>
            <Styled.MainWrapper ref={containerRef}>
                <Stack>
                    <Styled.SectionHeadingM>
                        {t("employerContributionDialog.monthlyContributionChange")}
                    </Styled.SectionHeadingM>

                    <Styled.StackBetween>
                        <Styled.SubHeadingM>{t("employerContributionDialog.currentAmount")}</Styled.SubHeadingM>
                        <HeadingXS>
                            {isLoading ? (
                                <Styled.InlineBlockSkeleton />
                            ) : (
                                NumberToCZK(currentValues.currentContribution ?? 0)
                            )}
                        </HeadingXS>
                    </Styled.StackBetween>

                    <Stack gap={4}>
                        <Card>
                            <Styled.StackBetween>
                                <Styled.SectionHeadingXS>
                                    {t("employerContributionDialog.newAmount")}
                                </Styled.SectionHeadingXS>
                                <Styled.FixedWidthTextInput
                                    name="newContributionTextField"
                                    afterChange={(value) => {
                                        // Remove spaces and convert to number
                                        const newValue = Number(String(value).replace(/\s/g, ""));
                                        if (newValue !== contributionSlider) {
                                            //set slider to same value as text (only if not currently same)
                                            setValue("newContributionSliderField", newValue, { shouldValidate: true });
                                        }
                                    }}
                                    disabled={isLoading}
                                />
                            </Styled.StackBetween>
                            <Styled.SliderStack>
                                {isLoading ? (
                                    <Styled.SliderSkeleton />
                                ) : (
                                    <SliderInput
                                        name="newContributionSliderField"
                                        min={MIN_CONTRIBUTION}
                                        max={MAX_CONTRIBUTION}
                                        minText={NumberToCZK(MIN_CONTRIBUTION)}
                                        maxText={NumberToCZK(MAX_CONTRIBUTION)}
                                        afterChange={(value) => {
                                            //set text field to same value as slider (only if not currently same)
                                            const newValue = Number(value);
                                            if (newValue !== contributionText) {
                                                setValue("newContributionTextField", newValue, {
                                                    shouldValidate: true,
                                                });
                                            }
                                        }}
                                    />
                                )}
                            </Styled.SliderStack>
                        </Card>
                        <Card>
                            <Stack gap={4}>
                                <Styled.StackBetween>
                                    <BodyTextM>
                                        {t("employerContributionDialog.possibleGovernmentContribution")}
                                    </BodyTextM>
                                    <Styled.SectionHeadingXS>
                                        {NumberToCZK(possibleGovContribution)}
                                    </Styled.SectionHeadingXS>
                                </Styled.StackBetween>
                                <Styled.StackBetween>
                                    <BodyTextM>{t("employerContributionDialog.possibleTaxSaving")}</BodyTextM>
                                    <Styled.SectionHeadingXS>{NumberToCZK(possibleTaxSaving)}</Styled.SectionHeadingXS>
                                </Styled.StackBetween>
                            </Stack>
                        </Card>
                    </Stack>
                </Stack>

                <Styled.EmployerSectionStack ref={employerSectionRef}>
                    <Stack>
                        <Styled.SectionHeadingM>
                            {t("employerContributionDialog.employerChange")}
                        </Styled.SectionHeadingM>
                        <Styled.NoMarginBodyTextM>
                            {t("employerContributeDialog.currentValue")}{" "}
                            <Styled.BoldBodyTextM as="span">{currentEmployerContributionText}</Styled.BoldBodyTextM>
                        </Styled.NoMarginBodyTextM>
                    </Stack>

                    <Card>
                        <SwitchInput
                            name="employerContributes"
                            label={
                                employerContributes === true
                                    ? t("employerContributeDialog.employerContributes")
                                    : t("employerContributeDialog.employerDoesNotContribute")
                            }
                            disabled={isLoading}
                        />
                    </Card>

                    <Alert severity="warning">{t("employerContributionDialog.legalInfoWarning")}</Alert>
                    <Styled.TextSMuted>{t("employerContributionDialog.legalInfoShorter")}</Styled.TextSMuted>

                    <Stack ref={unchangedErrorRef}>
                        {!isModified && (
                            <FormError
                                error={{
                                    type: "error",
                                    message: t("errorMessages.makeChanges"),
                                }}
                            />
                        )}
                    </Stack>
                </Styled.EmployerSectionStack>
            </Styled.MainWrapper>
        </FormProvider>
    );
};
